// src/components/store/QuickCartComponents/DeliveryDateSelector.jsx
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@radix-ui/themes";
import { Calendar, Sun, CloudSun } from "lucide-react";
import { useSalesContext } from "../../../contexts/SalesContext";
import { DELIVERY_CONFIG } from "../../../config/delivery";
import { formatDate } from "../../../scripts/generalScript";

const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .smooth-scroll {
    scroll-behavior: smooth;
  }
  .no-snap {
    scroll-snap-type: none;
  }
  .mobile-snap {
    scroll-snap-type: x mandatory;
  }
  @media (max-width: 768px) {
    .mobile-snap {
      scroll-snap-type: x mandatory;
    }
  }
  @media (min-width: 769px) {
    .mobile-snap {
      scroll-snap-type: none;
    }
  }
`;

export const DeliveryDateSelector = () => {
  const {
    selectedDeliveryDate,
    setSelectedDeliveryDate,
    selectedTimeSlot,
    setSelectedTimeSlot,
  } = useSalesContext();

  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlotKey, setSelectedSlotKey] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);

  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  };

  const calculateAvailableSlots = useCallback((orderTime = new Date()) => {
    const slots = [];
    const currentHour = orderTime.getHours();
    const orderDateTime = orderTime.getTime();

    const minDeliveryTime = new Date(
      orderDateTime + DELIVERY_CONFIG.MINIMUM_PREP_HOURS * 60 * 60 * 1000
    );

    let checkDate = new Date(orderTime);
    checkDate.setDate(checkDate.getDate() + 1);

    for (let day = 0; day < 10; day++) {
      const currentCheckDate = new Date(checkDate);
      currentCheckDate.setDate(checkDate.getDate() + day);

      if (currentCheckDate.getDay() === 0) continue;

      const daySlots = [];

      const morningSlot = new Date(currentCheckDate);
      morningSlot.setHours(DELIVERY_CONFIG.MORNING_SLOT.start, 0, 0, 0);

      if (morningSlot.getTime() >= minDeliveryTime.getTime()) {
        if (
          day > 0 ||
          currentHour < DELIVERY_CONFIG.CUTOFF_TIMES.NEXT_DAY_MORNING_CUTOFF
        ) {
          daySlots.push({
            ...DELIVERY_CONFIG.MORNING_SLOT,
            available: true,
            urgency: day === 0 ? "soon" : "normal",
          });
        }
      }

      const afternoonSlot = new Date(currentCheckDate);
      afternoonSlot.setHours(DELIVERY_CONFIG.AFTERNOON_SLOT.start, 0, 0, 0);

      if (afternoonSlot.getTime() >= minDeliveryTime.getTime()) {
        daySlots.push({
          ...DELIVERY_CONFIG.AFTERNOON_SLOT,
          available: true,
          urgency: day === 0 ? "soon" : "normal",
        });
      }

      if (daySlots.length > 0) {
        slots.push({
          date: new Date(currentCheckDate),
          slots: daySlots,
          dayOfWeek: currentCheckDate.getDay(),
          isToday: isSameDay(currentCheckDate, new Date()),
          isTomorrow: isSameDay(currentCheckDate, getTomorrow()),
        });
      }
    }
    return slots.slice(0, 8);
  }, []);

  useEffect(() => {
    const slots = calculateAvailableSlots();
    setAvailableSlots(slots);
  }, [calculateAvailableSlots]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        const scrollAmount = e.deltaY * 3;
        container.scrollBy({
          left: scrollAmount,
          behavior: "auto",
        });
      }
    };

    const handleMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      container.style.cursor = "grabbing";
      container.style.userSelect = "none";
      container.style.scrollBehavior = "auto";
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1.5;
      container.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      container.style.cursor = "grab";
      container.style.userSelect = "auto";
      container.style.scrollBehavior = "smooth";
    };

    const handleMouseLeave = () => {
      setIsDragging(false);
      container.style.cursor = "grab";
      container.style.userSelect = "auto";
      container.style.scrollBehavior = "smooth";
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseLeave);

    container.style.cursor = "grab";
    container.style.scrollBehavior = "smooth";

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isDragging, startX, scrollLeft]);

  useEffect(() => {
    if (selectedDeliveryDate && selectedTimeSlot) {
      setSelectedSlotKey(
        `${selectedDeliveryDate.toDateString()}-${selectedTimeSlot}`
      );
    } else if (!selectedDeliveryDate && !selectedTimeSlot) {
      setSelectedSlotKey(null);
    }
  }, [selectedDeliveryDate, selectedTimeSlot]);

  const handleSlotSelection = (dayData, slot) => {
    const slotKey = `${dayData.date.toDateString()}-${slot.code}`;
    if (selectedSlotKey === slotKey) {
      setSelectedSlotKey(null);
      setSelectedDeliveryDate(null);
      setSelectedTimeSlot(null);
    } else {
      setSelectedSlotKey(slotKey);
      setSelectedDeliveryDate(dayData.date);
      setSelectedTimeSlot(slot.code);
    }
  };

  const isSlotSelected = (dayData, slot) => {
    const slotKey = `${dayData.date.toDateString()}-${slot.code}`;
    return selectedSlotKey === slotKey;
  };

  const isNextAvailable = (dayData, slot) => {
    if (availableSlots.length === 0) return false;
    const firstDay = availableSlots[0];
    const firstSlot = firstDay.slots[0];
    return (
      isSameDay(dayData.date, firstDay.date) && slot.code === firstSlot.code
    );
  };

  const getSlotColor = (slot, isSelected) => {
    if (isSelected) {
      return "bg-blue-500 text-white";
    }
    return "bg-gray-200 text-gray-700 hover:bg-blue-200";
  };

  const getDayBadge = (dayData) => {
    if (dayData.isToday)
      return { text: "HOY", color: "bg-blue-500 text-white" };
    if (dayData.isTomorrow)
      return { text: "MAÑANA", color: "bg-green-500 text-white" };
    return null;
  };

  const shouldReduceCardOpacity = (dayData) => {
    if (!selectedSlotKey) return false;
    const cardKey = dayData.date.toDateString();
    const selectedCardKey = selectedDeliveryDate?.toDateString();
    const isHovered = hoveredCard === cardKey;
    return selectedCardKey !== cardKey && !isHovered;
  };

  const shouldReduceSlotOpacity = (dayData, slot) => {
    if (!selectedSlotKey) return false;
    const slotKey = `${dayData.date.toDateString()}-${slot.code}`;
    const isHovered =
      hoveredSlot === slotKey || hoveredCard === dayData.date.toDateString();
    const isSelected = selectedSlotKey === slotKey;
    return !isSelected && !isHovered;
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: scrollbarHideStyles }} />
      <div className="flex flex-col gap-8 mt-8">
        <div className="overflow-visible">
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory md:snap-none smooth-scroll z-[9999999]"
            style={{
              scrollPadding: "1rem",
              scrollBehavior: "smooth",
              paddingRight: "1rem",
            }}
          >
            {availableSlots.map((dayData, dayIndex) => {
              const dateFormatted = formatDate(dayData.date, true);
              const dayBadge = getDayBadge(dayData);
              const hasSelectedSlot = dayData.slots.some((slot) =>
                isSlotSelected(dayData, slot)
              );
              const cardKey = dayData.date.toDateString();
              const shouldReduceOpacity = shouldReduceCardOpacity(dayData);

              return (
                <motion.div
                  key={dayData.date.toDateString()}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: dayIndex * 0.1 }}
                  onMouseEnter={() => setHoveredCard(cardKey)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className={`snap-start flex-shrink-0 w-[224px] rounded-xl p-4 transition-all duration-200 h-64 ${
                    hasSelectedSlot
                      ? "bg-blue-50 "
                      : "bg-gray-50 hover:border-gray-50"
                  } ${shouldReduceOpacity ? "opacity-80" : "opacity-100"}`}
                >
                  <div className="text-center flex flex-col items-center mb-4 relative">
                    {dayBadge ? (
                      <div className="text-xs text-green-100 bg-green-600 p-1 rounded-xl font-medium max-w-fit px-2">
                        {dayBadge.text}
                      </div>
                    ) : (
                      <div className="text-base text-stone-800 font-medium">
                        {dateFormatted.dayName}
                      </div>
                    )}
                    <div className="text-4xl font-bold text-gray-800 my-1 fraunces">
                      {dateFormatted.dayNumber}
                    </div>
                    <div className="text-base text-gray-500">
                      {dateFormatted.month}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {dayData.slots.map((slot) => {
                      const isSelected = isSlotSelected(dayData, slot);
                      const isNext = isNextAvailable(dayData, slot);
                      const slotKey = `${dayData.date.toDateString()}-${
                        slot.code
                      }`;
                      const shouldReduceSlotOp = shouldReduceSlotOpacity(
                        dayData,
                        slot
                      );

                      return (
                        <motion.button
                          key={slot.code}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onMouseEnter={() => setHoveredSlot(slotKey)}
                          onMouseLeave={() => setHoveredSlot(null)}
                          onClick={() => handleSlotSelection(dayData, slot)}
                          className={`w-full px-3 py-3 rounded-lg text-base font-medium transition-all duration-200 flex items-center justify-center gap-1 relative ${getSlotColor(
                            slot,
                            isSelected
                          )} ${
                            shouldReduceSlotOp ? "opacity-40" : "opacity-100"
                          }`}
                        >
                          {isNext && (
                            <div className="absolute -top-2 -right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-lg z-10">
                              Próximo
                            </div>
                          )}

                          {slot.label === "Por la tarde" ? (
                            <CloudSun size={14} />
                          ) : (
                            <Sun size={14} />
                          )}
                          <span>{slot.label}</span>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <AnimatePresence>
          {selectedDeliveryDate && selectedTimeSlot && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gray-50 rounded-lg p-4 flex flex-col md:flex-row gap-2 justify-between items-center"
            >
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="text-gray-5800" size={20} />
                <span className="text-gray-800">Recibiras tu pedido:</span>
              </div>
              <div className="text-base flex flex-col md:flex-row md:gap-4 text-gray-700 items-center">
                <p className=" font-medium">
                  {formatDate(selectedDeliveryDate)}
                </p>
                <p>
                  {selectedTimeSlot === "morning"
                    ? DELIVERY_CONFIG.MORNING_SLOT.time
                    : DELIVERY_CONFIG.AFTERNOON_SLOT.time}
                </p>
                <Button
                  variant="soft"
                  className="mt-4 md:mt-0"
                  onClick={() => {
                    setSelectedDeliveryDate(null);
                    setSelectedTimeSlot(null);
                  }}
                >
                  Cambiar
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default DeliveryDateSelector;